import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIG
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// SERVICES
import { IAppIntegrationShippingPreset } from '../../intefaces/app-integration.interface';

import { AppIntegrationPresetApiService } from './../../../shared/services/api/app-integration/app-integration-preset-api.service';

@Component({
  selector: 'lum-app-integration-preset-form',
  templateUrl: './app-integration-preset-form.component.html',
  styleUrls: ['./app-integration-preset-form.component.scss'],
})
export class AppIntegrationPresetFormComponent implements OnInit, OnDestroy {
  @Input()
  appIntegrationAccountId :number | null = null;

  @Input()
  selectedPreset :IAppIntegrationShippingPreset | null = null;

  @Output()
  cancel :EventEmitter<IAppIntegrationShippingPreset | null> =
    new EventEmitter<IAppIntegrationShippingPreset | null>();

  form :UntypedFormGroup;

  private submittingForm$ = new BehaviorSubject<boolean>(false);

  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private appIntegrationPresetApiService :AppIntegrationPresetApiService,
    private snackbar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initForm();
  }

  ngOnDestroy() :void {
    this.form.reset();
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onCancel() :void {
    this.form.reset();
    this.cancel.emit();
  }

  onSubmit(value :any) :void {
    this.submittingForm$.next(true);

    if (!this.selectedPreset) {
      this.appIntegrationPresetApiService
        .create({
          appIntegrationAccountId: this.appIntegrationAccountId,
          name: this.form.get('name')?.value,
          ...value,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => {
            this.form.reset();
            this.submittingForm$.next(false);
          }),
        )
        .subscribe({
          next: (shippingPreset :IAppIntegrationShippingPreset) => {
            this.snackbar.open('Preset created successfully', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.cancel.emit(shippingPreset);
          },
          error: (err) => {
            this.snackbar.open(err.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    } else {
      this.appIntegrationPresetApiService
        .update(this.selectedPreset.id, {
          appIntegrationAccountId: this.appIntegrationAccountId,
          name: this.form.get('name')?.value,
          ...value,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.submittingForm$.next(false)),
        )
        .subscribe({
          next: (shippingPreset :IAppIntegrationShippingPreset) => {
            this.snackbar.open('Preset updated successfully', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.cancel.emit(shippingPreset);
          },
          error: (err) => {
            this.snackbar.open(err.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  private initForm() :void {
    this.form = this.fb.group({
      name: [this.selectedPreset?.name || '', Validators.required],
    });
  }

  get nameControl() :UntypedFormControl {
    return this.form.get('name') as UntypedFormControl;
  }

  get submittingForm() :boolean {
    return this.submittingForm$.getValue();
  }
}
